import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { STAFF_EMPTY, STAFF_SECTIONS, GENRES } from "@/lib/staffFields";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function StaffModal({ open, onOpenChange, item, onSaved, apiBase = "/staff", extraSections = [] }) {
  const [form, setForm] = useState(STAFF_EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(item);

  useEffect(() => {
    if (open) setForm(item ? { ...STAFF_EMPTY, ...item } : STAFF_EMPTY);
  }, [open, item]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nom.trim() || !form.prenom.trim()) {
      toast.error("Nom et prénom obligatoires");
      return;
    }
    const payload = {};
    Object.keys(STAFF_EMPTY).forEach((k) => (payload[k] = form[k] ?? ""));
    payload.doc_synology = Boolean(form.doc_synology);
    payload.caces_doc_synology = Boolean(form.caces_doc_synology);
    payload.vm_en_attente = Boolean(form.vm_en_attente);
    setSaving(true);
    try {
      if (editing) await api.put(`${apiBase}/${item.id}`, payload);
      else await api.post(apiBase, payload);
      toast.success(editing ? "Fiche mise à jour" : "Fiche ajoutée");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (k, label) => {
    if (k === "doc_synology" || k === "caces_doc_synology") {
      return (
        <div key={k} className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <Select value={form[k] ? "oui" : "non"} onValueChange={(v) => set(k, v === "oui")}>
            <SelectTrigger data-testid={`staff-field-${k}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="oui">Oui</SelectItem>
              <SelectItem value="non">Non</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (k === "caces") {
      return (
        <div key={k} className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <Select value={form.caces || "none"} onValueChange={(v) => set("caces", v === "none" ? "" : v)}>
            <SelectTrigger data-testid={`staff-field-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="OUI">OUI</SelectItem>
              <SelectItem value="NON">NON</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (k === "genre") {
      return (
        <div key={k} className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <Select value={form.genre || ""} onValueChange={(v) => set("genre", v)}>
            <SelectTrigger data-testid={`staff-field-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }
    return (
      <div key={k} className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`staff-field-${k}`} />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="staff-modal">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Modifier la fiche" : "Nouvelle fiche"}</DialogTitle>
          <DialogDescription>Fiche complète.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {[...STAFF_SECTIONS, ...extraSections].map((section) => (
            <div key={section.title}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">{section.title}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.fields.map(([k, label]) => renderField(k, label))}
              </div>
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="staff-field-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="staff-save-button">
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
